import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { UserType } from "@/shared/types/user.entity";

import { MetricsService } from "../services/metrics.service";

// The allowlist is read at backend startup, so one answer per user and session is enough.
const answers = new Map<string, boolean>();
const inflight = new Map<string, Promise<boolean>>();

function ensureAccess(userId: string): Promise<boolean> {
  const known = answers.get(userId);
  if (known !== undefined) return Promise.resolve(known);
  let promise = inflight.get(userId);
  if (!promise) {
    promise = MetricsService.access()
      .then((allowed) => {
        answers.set(userId, allowed);
        return allowed;
      })
      .finally(() => inflight.delete(userId));
    inflight.set(userId, promise);
  }
  return promise;
}

export function useMetricsAccess(): { allowed: boolean; isLoading: boolean } {
  const { user } = useAuth();
  const userId = user?.user_type === UserType.INTERNAL ? user.id : null;
  const [read, setRead] = useState<{ userId: string; allowed: boolean } | null>(() =>
    userId && answers.has(userId) ? { userId, allowed: answers.get(userId) === true } : null,
  );

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    ensureAccess(userId)
      .catch(() => false)
      .then((allowed) => {
        if (!cancelled) setRead({ userId, allowed });
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!userId) return { allowed: false, isLoading: false };
  if (read?.userId !== userId) return { allowed: false, isLoading: true };
  return { allowed: read.allowed, isLoading: false };
}
